import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ILocalizedInformation, IPriceFormat } from '@inter-ikea-kompis/types';
import { DexfSettingsUtility, PriceFormatterUtility } from '@inter-ikea-kompis/utilities';
import { EasingAnimation } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import type IPriceFormatterDexfSettings from '../../types/IPriceFormatterDexfSettings.js';
import PriceFormatterStyle from './PriceFormatterStyle.js';

/**
 * Price View component that formats price.
 */

export default class PriceFormatter extends KompisElement {
	// Localized information. Deprecated - use "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IPriceFormatterDexfSettings | null = null;

	// Mixed sizes.
	@property({ type: Boolean })
	public mixedSize = false;

	// Strikethrough.
	@property({ type: Boolean })
	public strikeThrough = false;

	// Set price to be animated
	@property({ type: Boolean })
	public animated = false;

	// Label used to describe the price Example: Regular price.
	@property({ type: String })
	public ariaLabelPrice: string | null = null;

	// The amount of money that should be formatted according to the locale
	@property({ type: Number, required: true })
	public amount!: number;

	// Overrides the currencySymbol in the Price Settings of DEXF settings. Used in PriceModule when dual currencies are provided where DEXF settings keys such as currencySymbolAlt and currencyCodeAlt are taken into consideration.
	@property({ type: String })
	public currencySymbol: string | null = null;

	private animation: EasingAnimation | null = null;
	private animatedAmount = 0;

	private dexfSettings: IPriceFormatterDexfSettings | null = null;

	public override render(): RenderResult {
		// We want settings to be required, but since both props to provide settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		const settings = this.getSettings();
		const {
			localisation,
			kompis: { priceSettings }
		} = settings;

		if (this.currencySymbol) {
			localisation.currencySymbol = this.currencySymbol;
		}

		const items = PriceFormatterUtility.format(this.animatedAmount || 0, settings);

		const currencySymbol =
			this.currencySymbol === priceSettings.currencySymbolAlt
				? priceSettings.currencyCodeAlt
				: localisation.currencyCode;
		const parts: RenderResult[] = [];
		const srText =
			items
				.filter(({ type }) => type !== 'currency')
				.map(({ value }) => value)
				.join('') + currencySymbol;

		for (let i = 0; i < items.length; i++) {
			if (i !== 0 && items[i].type === 'currency') {
				parts.push(html`<span class="currencySpacing"></span>`);
			}

			parts.push(html`<span class="${items[i].type}">${this.handleBreakingSpace(items[i])}</span>`);

			if (i === 0 && items[i].type === 'currency') {
				parts.push(html`<span class="currencySpacing"></span>`);
			}
		}

		return html`
			<style>
				${PriceFormatterStyle.getStyle({
					mixedSize: this.mixedSize,
					strikeThrough: this.strikeThrough,
					decimalVerticalAlignment: priceSettings.decimalVerticalAlignment,
					currencyVerticalAlignment: priceSettings.currencyVerticalAlignment
				})}
			</style>
			<!-- Do not remove dir=ltr! This has been removed several times now causing problems
			with price formatting not being rendered correcly in RTL markets according to Översätta rules. -->
			<div dir="ltr" class="container">
				<span class="price" aria-hidden="true">${parts}</span>
				<span class="visuallyHidden">
					${this.ariaLabelPrice ? this.ariaLabelPrice + ' ' + srText : srText}
				</span>
			</div>
		`;
	}

	/**
	 * Triggered when the component is removed from DOM.
	 */
	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		if (this.animation) {
			this.animation.stop();
			this.animation = null;
		}
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('amount') || changedProperties.has('animated')) {
			const animate = this.animated && this.hasAnimationSupport();
			if (animate) {
				this.startAnimation();
			} else {
				if (this.animation) {
					this.animation.stop();
					this.animation = null;
				}
				this.animatedAmount = this.amount || 0;
			}
		}

		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = <IPriceFormatterDexfSettings>(
				DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation)
			);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = {
				kompis: this.settings.kompis,
				localisation: Object.assign({}, this.settings.localisation)
			};
		}

		return true;
	}

	private hasSettings(): boolean {
		return !!this.dexfSettings || !!this.localizedInformation;
	}

	private getSettings(): IPriceFormatterDexfSettings {
		return <IPriceFormatterDexfSettings>this.dexfSettings;
	}

	/**
	 * Replace ' ' with &nbsp; to avoid RTL issues.
	 *
	 * @param priceFormat ...
	 */
	private handleBreakingSpace(priceFormat: IPriceFormat): unknown[] {
		return this.addOddIndexes(priceFormat.value.split(' '), html`&nbsp;`).filter(
			(value) => value !== ''
		);
	}

	/**
	 * Adds something between all elements in a string array. Similar to Array.prototype.join().
	 *
	 * @param list ...
	 * @param value ...
	 */
	private addOddIndexes(list: string[], value: RenderResult): RenderResult[] {
		if (list.length === 0) {
			return [value];
		}

		if (list.length === 1) {
			return list;
		}

		const newList: RenderResult[] = [];

		for (let index = 0; index < list.length; index++) {
			newList.push(list[index]);
			if (index !== list.length - 1) {
				newList.push(value);
			}
		}

		return newList;
	}

	/**
	 * Resets and start animation this.__animation.
	 */
	private startAnimation(): void {
		const newAnimation = this.getAnimation();

		newAnimation.startValue = this.animatedAmount;
		newAnimation.endValue = this.amount || 0;
		newAnimation.start();

		if (this.animation) {
			this.animation.stop();
		}

		this.animation = newAnimation;
	}

	/**
	 * Checks if the device has JavaScript animation support.
	 * IOS9 and less runs animations very slow so therefore it will considered as not having support.
	 *
	 * @returns TRUE if the device has animation support.
	 */
	private hasAnimationSupport(): boolean {
		const regexp = /(iPhone|iPad)*CPU OS ([0-9]+)_/;
		const match = navigator.userAgent.match(regexp);
		const iosVersion = match && match.length > 2 ? parseFloat(match[2]) : null;

		return iosVersion === null || iosVersion > 9;
	}

	/**
	 * Creates Animation.
	 *
	 * @returns An animation object.
	 */
	private getAnimation(): EasingAnimation {
		const animation = new EasingAnimation();

		animation.on('frame', (amount) => {
			this.animatedAmount = amount;
			this.requestUpdate();
		});
		animation.on('end', () => {
			this.animatedAmount = this.amount || 0;
			this.animation = null;
			this.requestUpdate();
		});

		return animation;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-price-formatter': PriceFormatter;
	}
}
